import Quiz from './quiz';
import Form from './form';
import Map from './map';
import ResultsList from './results-list';
import Header from '../../global/header/header';

const settings = {
    selectors: {
        // Top-level container
        container: '#surgeon-locator',

        // Quiz modal
        quizButton: '.surgeon-locator__introduction button',

        // Form container
        form: '.surgeon-locator__form',

        // Results containers
        map: '.surgeon-locator__map > div',
        resultsList: '.surgeon-locator__results',
    },

    classNames: {
        resultsAvailable: 'surgeon-locator--results-available',
        noResultsFound: 'surgeon-locator--no-results',
    },
};

export default class SurgeonLocator {
    constructor(container) {
        if (!container) {
            return;
        }

        this.container = container;
        this.initialSearchPerformed = false;

        // Get references to our DOM elements
        this.dom = {
            quizButton: document.querySelector(settings.selectors.quizButton),
            form: container.querySelector(settings.selectors.form),
            map: container.querySelector(settings.selectors.map),
            resultsList: container.querySelector(
                settings.selectors.resultsList,
            ),
        };

        // Setup quiz
        this.quiz = new Quiz({
            searchCallback: (userResponses) => {
                // Use the Quiz output to popuplate the form
                this.form.prefillFromQuizResponses(userResponses);

                // Perform search
                if (this.form.validate()) {
                    this.performSearch();
                }
            },
        });

        this.dom.quizButton.addEventListener('click', (event) => {
            event.preventDefault();
            this.quiz.show();
        });

        // Setup form
        this.form = new Form(this.dom.form, {
            canAutoSearchOnInputChanges: () => {
                return this.initialSearchPerformed;
            },

            searchCallback: () => {
                // Perform search
                this.performSearch();
            },
        });

        // Setup map and results list
        this.map = new Map(this.dom.map);
        this.resultsList = new ResultsList(this.dom.resultsList, {
            resultHoverCallback: (hover, surgeonID) => {
                // Highlight the map markers associated with the specified ID
                this.map.highlightMarkersForSurgeonId(hover, surgeonID);
            },
        });

        // Perform an initial search, if we're ready
        if (this.form.validate()) {
            this.performSearch(false);
        }
    }

    static init() {
        // Need to wait for Google maps API to be available
        document.addEventListener('DOMContentLoaded', () => {
            const elements = document
                .querySelectorAll(settings.selectors.container);
            for (const element of elements) {
                new SurgeonLocator(element);
            }
        });
    }

    performSearch(scrollToMap = true) {
        // Collect form values
        const queryParameters = this.form.lastValidatedQueryParameters;
        if (!queryParameters) {
            return;
        }

        // Update query string to reflect current form state
        const url = new URL(window.location.href);
        url.search = '';
        for (const key of Object.keys(queryParameters)) {
            const value = queryParameters[key];
            if (value) {
                url.searchParams.set(key, value);
                url.searchParams.sort();
            } else {
                url.searchParams.delete(key);
            }
        }

        history.replaceState({}, '', url);

        // Perform AJAX call to our search endpoint
        const postData = {
            action: 'get_surgeon_locations',
            ...queryParameters,
        };

        fetch('/wp-admin/admin-ajax.php', {
            method: 'POST',
            headers: {
                'content-type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams(postData),
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error: ${response.status}`);
                }

                // Success - retrieve the results
                return response.json();
            })
            .then((surgeonResults) => {
                // Process the results
                ajaxCallback.call(this, surgeonResults, scrollToMap);
            })
            .catch((error) => {
                // Not sure offhand how we'd like to handle errors
                // ...
            });
    }
}

/*
    Internal functions
*/

/**
 * @this SurgeonLocator
 * @param {*} surgeonResults
 * @param {*} scrollToMap
 */
function ajaxCallback(surgeonResults, scrollToMap) {
    // Flag that an initial search has been performed - this is used by form.js
    // to skip searches due to filter & distance input changes until a search
    // has been performed
    this.initialSearchPerformed = true;

    // Process & update results list
    this.resultsList.setResultsForApiResponse(surgeonResults);

    // Update map markers
    this.map.deleteMarkers();
    for (const location of this.resultsList.resultsByLocation) {
        // Sort surgeons by last name
        location.surgeons.sort((a, b) => {
            if (a.lastname < b.lastname) {
                return -1;
            } else if (a.lastname > b.lastname) {
                return 1;
            }

            if (a.firstname < b.firstname) {
                return -1;
            } else if (a.firstname > b.firstname) {
                return 1;
            }

            return 0;
        });

        const surgeonIDs = [];
        let concatenatedHtml = '';
        for (const surgeonResult of location.surgeons) {
            surgeonIDs.push(surgeonResult.surgeonID);

            const element = ResultsList.createResultElement(surgeonResult);
            concatenatedHtml += element.outerHTML;
        }

        this.map.addMarker(
            location.lat,
            location.lng,
            concatenatedHtml,
            JSON.stringify(surgeonIDs),
        );
    }

    // Check if results were available
    if (this.resultsList.resultsBySurgeon.length > 0) {
        // Reults available - hide "no results" message
        this.container.classList.remove(settings.classNames.noResultsFound);

        // Show results list
        this.container.classList.add(settings.classNames.resultsAvailable);

        // Recenter the map based on the available markers
        this.map.recenter();

        // Scroll the map onscreen, if specified
        if (scrollToMap === true) {
            const bounds = this.dom.map.getBoundingClientRect();
            window.scrollTo({
                left: 0,
                top: (bounds.top + window.pageYOffset) - Header.height - 16,
                behavior: 'smooth',
            });
        }
    } else {
        // No results available - show "no results" message
        this.container.classList.add(settings.classNames.noResultsFound);

        // Hide results list
        this.container.classList.remove(settings.classNames.resultsAvailable);
    }
}
